import { Tooltip } from './Tooltip'
import './avatar.css'

/**
 * Somebody, as a mark.
 *
 * Initials rather than a picture: this app holds no photographs, and a name is
 * what it does know. Round, because it is a LABEL ON content — the one thing
 * `--radius-full` is for — and never a container of anything.
 *
 * It carries no color. Tinting an avatar by hashing a name is how a monochrome
 * interface acquires a palette nobody chose, and the initials already tell two
 * people apart.
 */
export function Avatar({
  name,
  size = 'md',
  onSelect,
  marked = false,
  led = false,
}: {
  /** Whatever is known — a full name, or the address of somebody yet to sign in. */
  name: string
  /** `sm` sits on a line of text; `md` stands on its own in chrome. */
  size?: 'sm' | 'md'
  /**
   * What choosing this person does, where they can be chosen.
   *
   * Given, the mark becomes a BUTTON and stops being decorative — so it takes
   * the name it was hiding, because a control the keyboard can reach has to say
   * who it is. Without it the mark stays `aria-hidden` and the name comes from
   * the text beside it, which is what every other place in the app does.
   */
  onSelect?: () => void
  /**
   * Whether this is the one being acted on — the reader being followed.
   *
   * Ink, not `--highlight`: that token is the focus ring, and on a round mark
   * the natural gold treatment IS a ring, so a keyboard user could not tell the
   * one they are on from the one they chose.
   */
  marked?: boolean
  /**
   * Whether this person is LEADING the room.
   *
   * A ring rather than a fill, so it can be told from `marked` — you can be
   * following the leader, and both marks then land on one disc. Ink for the
   * same reason `marked` is ink: `--highlight` is the focus ring and may not be
   * spent saying anything else.
   */
  led?: boolean
}) {
  const className = [
    'avatar',
    `avatar--${size}`,
    marked ? 'avatar--marked' : '',
    led ? 'avatar--led' : '',
  ]
    .filter(Boolean)
    .join(' ')

  if (!onSelect) {
    return (
      <span className={className} aria-hidden>
        {initialsOf(name)}
      </span>
    )
  }

  return (
    <button
      type="button"
      className={className}
      aria-label={name}
      aria-pressed={marked}
      onClick={onSelect}
    >
      {initialsOf(name)}
    </button>
  )
}

/**
 * Two letters from a name, or one from an address.
 *
 * Split on whitespace AND `@`, because a person may carry an address as their
 * name — and "sh" from a mail host names the host rather than the person.
 */
export function initialsOf(name: string): string {
  return (
    name
      .split(/[\s@]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('') || '?'
  )
}

/**
 * The people in a room, as a stack.
 *
 * **Three of eleven**, which is why this is not an `OverflowGroup`: that
 * primitive collapses a group WHOLE and says, as its design, that nothing in it
 * reads how many children it was given. A stack is the opposite — it shows some
 * and states how many it did not — so building on it would smuggle
 * count-dependence into the one component written to have none.
 *
 * **It changes shape when there is something to do with somebody**, and that is
 * a semantic change rather than a styling one. Given no `onSelect` it is one
 * graphic — a `role="img"` carrying the whole room as its label, because the
 * discs are initials and a number that name nobody on their own. Given one, the
 * faces become buttons, and it must stop being an image: a `role="img"` hides
 * its subtree, so focusable children inside one are reachable by a keyboard and
 * announced to nobody. It becomes a group, and each face takes its own name.
 */
export function AvatarGroup({
  readers,
  anonymous = 0,
  anonymousCapped = false,
  max = 3,
  direction = 'row',
  size = 'md',
  onSelect,
  marked = null,
  led = null,
}: {
  /** Everybody who can be named, in the order they should be read. */
  readers: { personId: string; name: string }[]
  /** How many are here who cannot be — counted rather than drawn one by one. */
  anonymous?: number
  /** Whether that count is a ceiling rather than a tally. */
  anonymousCapped?: boolean
  /** How many faces before the rest become a number. */
  max?: number
  /**
   * Which way the stack runs. It follows the chrome the group sits in rather
   * than the group's own taste: in a row of cells it is a row, and in a column
   * standing in a gutter it is a column — a horizontal stack hanging off a
   * vertical rail reads as a different kind of object beside it.
   */
  direction?: 'row' | 'column'
  /**
   * Which mark the stack is made of.
   *
   * `md` by default, because a stack stands on its own in chrome rather than
   * beside a name — and at `sm` two initials take most of the disc's width and
   * meet its curve. The overlap follows the size, so the rhythm holds at both.
   */
  size?: 'sm' | 'md'
  /** What choosing one of them does, where they can be chosen. */
  onSelect?: (personId: string) => void
  /** Which of them is being acted on — the one this reader is following. */
  marked?: string | null
  /** Which of them is leading the room, marked in every reader's roster. */
  led?: string | null
}) {
  if (readers.length === 0 && anonymous === 0) return null

  /*
   * Whoever is leading is always drawn, however many are here.
   *
   * A spotlight is a public act and its mark is public with it — so a leader
   * who happened to fall past the overflow would be marked in a face nobody can
   * see, which is the one case the mark exists for. The same holds for the
   * reader being followed: a mark behind a "+4" says nothing.
   */
  const first = readers.filter(
    (r) => r.personId === led || r.personId === marked,
  )
  const others = readers.filter((r) => !first.includes(r))
  const inOrder = [...first, ...others]

  const shown = inOrder.slice(0, max)
  const rest = inOrder.slice(max)
  const many = anonymousCapped ? `${anonymous}+` : `${anonymous}`
  const room = roomReads(readers, anonymous, anonymousCapped)

  return (
    <span
      className={`avatars avatars--${direction} avatars--${size}`}
      {...(onSelect
        ? { role: 'group', 'aria-label': room }
        : { role: 'img', 'aria-label': room })}
    >
      {shown.map((r) => (
        <Tooltip key={r.personId} label={r.name}>
          <span className="avatars__one">
            <Avatar
              name={r.name}
              size={size}
              marked={marked === r.personId}
              led={led === r.personId}
              {...(onSelect ? { onSelect: () => onSelect(r.personId) } : {})}
            />
          </span>
        </Tooltip>
      ))}
      {rest.length > 0 ? (
        <Tooltip label={rest.map((r) => r.name).join(', ')}>
          <span className="avatars__one">
            <span className={`avatar avatar--${size}`} aria-hidden>
              +{rest.length}
            </span>
          </span>
        </Tooltip>
      ) : null}
      {anonymous > 0 ? (
        <Tooltip label={`${many} reading by link, not signed in`}>
          <span className="avatars__one">
            {/*
             * An outline rather than a filled disc, because it is not somebody:
             * it is however many people a public link happens to have in the
             * room. The difference is geometry and ink — a state does not earn
             * a color by existing.
             */}
            <span
              className={`avatar avatar--${size} avatar--unnamed`}
              aria-hidden
            >
              {many}
            </span>
          </span>
        </Tooltip>
      ) : null}
    </span>
  )
}

/** What the stack says to somebody who cannot see it. */
function roomReads(
  readers: { name: string }[],
  anonymous: number,
  capped: boolean,
): string {
  const parts = readers.map((r) => r.name)
  if (anonymous > 0) {
    parts.push(
      `${capped ? `${anonymous} or more` : anonymous} reading by link, not signed in`,
    )
  }
  if (parts.length === 0) return 'Nobody else is reading this'
  return `Also reading: ${parts.join(', ')}`
}
